import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { SETUP_STEPS, STEP_LABELS, STEP_ROUTES, type SetupStep } from "@/types";
import { canProceedToCheckout, useSetupStore } from "@/lib/setup-state";

const STEP_BY_ROUTE: Record<string, SetupStep> = {
  "/signup": "package",
  "/choose-assistant": "agent",
  "/connections": "connections",
  "/define": "rules",
  "/review": "review",
  "/checkout/return": "checkout",
  "/connect": "activate",
  "/activate": "activate",
};

function canEnterStep(step: SetupStep): boolean {
  const state = useSetupStore.getState();
  if (step === "package") return true;
  if (step === "agent") return Boolean(state.selectedPlan);
  if (step === "connections") return Boolean(state.selectedAgent ?? state.selectedTemplate);
  if (step === "rules") return state.connections.some((connection) => connection.selected);
  if (step === "review") return Boolean(state.rules) && canProceedToCheckout(state);
  if (step === "checkout") return canProceedToCheckout(state);
  return state.provisioningStatus === "ready";
}

export function SetupLayout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const storedStep = useSetupStore((state) => state.currentStep);
  const currentStep = STEP_BY_ROUTE[location.pathname] ?? storedStep;
  const currentIndex = SETUP_STEPS.indexOf(currentStep);

  return (
    <div className="portal-shell">
      <header className="portal-topbar">
        <Link className="brand-mark" to="/signup" aria-label="Pulse Business Connectors home">
          <span className="brand-mark__glyph">P</span>
          <span>
            <strong>Pulse</strong>
            <small>Business Connectors</small>
          </span>
        </Link>
        <nav className="portal-nav" aria-label="Portal">
          <Link to="/assistants">Assistants</Link>
          <Link to="/billing">Billing</Link>
          <Link to="/support">Support</Link>
        </nav>
      </header>

      <nav className="stepper" aria-label="Assistant setup steps">
        {SETUP_STEPS.map((step, index) => {
          const enabled = canEnterStep(step);
          const isActive = step === currentStep;
          const isComplete = index < currentIndex;
          return (
            <button
              key={step}
              aria-current={isActive ? "step" : undefined}
              aria-label={`${STEP_LABELS[step]}, step ${index + 1} of ${SETUP_STEPS.length}`}
              className={[
                "stepper__step",
                isActive ? "stepper__step--active" : "",
                isComplete ? "stepper__step--completed" : "",
                !enabled ? "stepper__step--disabled" : "",
              ].join(" ")}
              disabled={!enabled}
              type="button"
              onClick={() => navigate(STEP_ROUTES[step])}
            >
              {isComplete ? <CheckCircle2 size={15} aria-hidden="true" /> : <span className="stepper__number">{index + 1}</span>}
              {STEP_LABELS[step]}
            </button>
          );
        })}
      </nav>

      <div aria-live="polite" className="sr-only">
        Current setup step: {STEP_LABELS[currentStep]}.
      </div>
      {children}
    </div>
  );
}
